import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import { Globe, MessageSquare, Mail, Phone, Shield, CheckCircle2, XCircle, AlertTriangle, Unplug } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const CHANNELS = [
  { type: 'website', label: 'Website-Widget', icon: Shield, fields: [] },
  { type: 'email', label: 'E-Mail', icon: Mail, fields: [
    { key: 'smtp_host', label: 'SMTP Host', placeholder: 'smtp.example.com' },
    { key: 'smtp_port', label: 'SMTP Port', placeholder: '587' },
    { key: 'smtp_user', label: 'SMTP Benutzer', placeholder: 'user@example.com' },
    { key: 'smtp_pass', label: 'SMTP Passwort', placeholder: '••••••••', type: 'password' },
    { key: 'imap_host', label: 'IMAP Host', placeholder: 'imap.example.com' },
  ]},
  { type: 'whatsapp', label: 'WhatsApp Business', icon: MessageSquare, fields: [
    { key: 'phone_number_id', label: 'Phone Number ID', placeholder: '491701234567', hint: 'Format: Nur Ziffern, mit Ländervorwahl (z.B. 491701234567)' },
    { key: 'access_token', label: 'Access Token', placeholder: 'EAAG...', type: 'password' },
    { key: 'waba_id', label: 'WABA ID', placeholder: '10XXXX' },
  ]},
  { type: 'telegram', label: 'Telegram', icon: Globe, fields: [
    { key: 'bot_token', label: 'Bot Token', placeholder: '123456:ABC-DEF', type: 'password', hint: 'Erhalten Sie den Token von @BotFather auf Telegram' },
  ]},
];

function getChannelStatus(channel, configs) {
  if (channel.type === 'website') return { status: 'connected', label: 'Verbunden' };
  const cfg = configs.find(c => c.type === channel.type);
  if (!cfg || !cfg.credentials || Object.keys(cfg.credentials).length === 0) return { status: 'not_configured', label: 'Nicht konfiguriert' };
  const required = channel.fields.filter(f => !f.key.includes('hint'));
  const filled = required.filter(f => cfg.credentials[f.key]?.trim());
  if (filled.length === 0) return { status: 'not_configured', label: 'Nicht konfiguriert' };
  if (filled.length < required.length) return { status: 'incomplete', label: 'Unvollständig' };
  return { status: 'ready', label: 'Bereit' };
}

const STATUS_STYLES = {
  connected: { color: '#10B981', icon: CheckCircle2 },
  ready: { color: '#3B82F6', icon: CheckCircle2 },
  incomplete: { color: '#F59E0B', icon: AlertTriangle },
  not_configured: { color: '#52525b', icon: XCircle },
};

export default function Channels() {
  const { user } = useAuth();
  const { t } = useI18n();
  const tenantId = user?.tenant_id;
  const [configs, setConfigs] = useState([]);
  const [editChannel, setEditChannel] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/tenants/${tenantId}/channels`).then(r => setConfigs(r.data)).catch(() => {});
  }, [tenantId]);

  const openEdit = (ch) => {
    const existing = configs.find(c => c.type === ch.type);
    const creds = existing?.credentials || {};
    setForm(creds);
    setEditChannel(ch);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}/channels/${editChannel.type}`, { credentials: form });
      toast.success(t.channelUpdated(editChannel.label));
      const r = await api.get(`/tenants/${tenantId}/channels`);
      setConfigs(r.data);
      setEditChannel(null);
    } catch { toast.error(t.error); }
    finally { setSaving(false); }
  };

  const handleDisconnect = async (ch) => {
    if (!window.confirm(t.disconnectConfirm(ch.label))) return;
    try {
      await api.put(`/tenants/${tenantId}/channels/${ch.type}`, { credentials: {} });
      toast.success(t.channelUpdated(ch.label));
      const r = await api.get(`/tenants/${tenantId}/channels`);
      setConfigs(r.data);
    } catch { toast.error(t.error); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[900px] mx-auto animate-fade-in-up">
      <div>
        <h2 className="font-heading font-bold text-2xl text-white" data-testid="channels-title">{t.channelsTitle}</h2>
        <p className="text-sm text-[#a1a1aa] mt-1">{t.channelsDesc}</p>
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
        <p className="text-xs text-[#71717a]">{t.channelArchitectureDesc}</p>
      </div>

      <div className="space-y-3">
        {CHANNELS.map(ch => {
          const { status, label } = getChannelStatus(ch, configs);
          const style = STATUS_STYLES[status];
          return (
            <div key={ch.type} className="bg-[#121214] border border-white/10 rounded-xl p-5 hover:border-white/15 transition-all" data-testid={`channel-${ch.type}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                    <ch.icon className="w-5 h-5 text-[#a1a1aa]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{ch.label}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <style.icon className="w-3.5 h-3.5" style={{ color: style.color }} />
                      <span className="text-xs font-medium" style={{ color: style.color }}>{label}</span>
                    </div>
                    {ch.type === 'website' && (
                      <p className="text-[10px] text-[#52525b] mt-0.5">{t.noExternalCredentials}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {status !== 'not_configured' && ch.fields.length > 0 && (
                    <button onClick={() => handleDisconnect(ch)} data-testid={`disconnect-${ch.type}`}
                      className="flex items-center gap-1 h-8 px-3 text-xs text-[#F43F5E] border border-[#F43F5E]/20 hover:bg-[#F43F5E]/10 rounded-lg transition-all">
                      <Unplug className="w-3 h-3" /> {t.disconnectChannel}
                    </button>
                  )}
                  {ch.fields.length > 0 && (
                    <button onClick={() => openEdit(ch)} data-testid={`configure-${ch.type}`}
                      className="h-8 px-4 bg-white/5 text-white text-xs font-medium border border-white/10 hover:bg-white/10 rounded-lg transition-all">
                      {t.configure}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-[#3f3f46]">{t.credentialsStored}</p>

      {/* Edit Dialog */}
      <Dialog open={!!editChannel} onOpenChange={() => setEditChannel(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md" data-testid="channel-config-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editChannel?.label} {t.configure}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {editChannel?.fields.map(f => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{f.label}</label>
                <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder} data-testid={`channel-field-${f.key}`}
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
                {f.hint && <p className="text-[10px] text-[#52525b] mt-1">{f.hint}</p>}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEditChannel(null)} className="flex-1 h-10 border border-white/10 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded-lg text-sm font-medium transition-all">{t.cancel}</button>
              <button onClick={handleSave} disabled={saving} data-testid="save-channel-btn"
                className="flex-1 h-10 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg text-sm transition-all disabled:opacity-60">{saving ? t.saving : t.save}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
